import React, { useEffect, useRef, useState } from 'react';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { GoogleAuthProvider, linkWithPopup, updatePassword } from 'firebase/auth';
import { auth } from '../services/firebase';
import { db } from '../services/firebase';
import type { GuidedClientAccess, User } from '../types';
import { getTranslation } from '../utils/translations';
import { upgradeGuidedClientToPro } from '../services/guidedClientService';
import XIcon from './icons/XIcon';

interface GoProModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User;
    onUpgraded: () => void;
}

const GoProModal: React.FC<GoProModalProps> = ({ isOpen, onClose, user, onUpgraded }) => {
    const t = (key: Parameters<typeof getTranslation>[1]) => getTranslation(user.language || 'en', key);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [accessId, setAccessId] = useState('');

    useEffect(() => {
        if (!isOpen || !user.guidedClientId) return;
        const q = query(
            collection(db, 'guidedClientAccess'),
            where('clientId', '==', user.guidedClientId),
        );
        const unsub = onSnapshot(q, (snap) => {
            const first = snap.docs[0];
            if (first) setAccessId(first.id);
        });
        return () => unsub();
    }, [isOpen, user.guidedClientId]);

    if (!isOpen) return null;

    const finishUpgrade = async () => {
        if (!user.guidedClientId) throw new Error('Missing guided client profile');
        await upgradeGuidedClientToPro(user.guidedClientId);
        onUpgraded();
        onClose();
    };

    const handleSetPassword = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        if (password.length < 6) {
            setError(t('goProPasswordTooShort'));
            return;
        }
        if (password !== confirmPassword) {
            setError(t('goProPasswordMismatch'));
            return;
        }
        setLoading(true);
        try {
            const current = auth.currentUser;
            if (!current) throw new Error('Not signed in');
            await updatePassword(current, password);
            await finishUpgrade();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('goProFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleLinkGoogle = async () => {
        setError('');
        setLoading(true);
        try {
            const current = auth.currentUser;
            if (!current) throw new Error('Not signed in');
            const provider = new GoogleAuthProvider();
            await linkWithPopup(current, provider);
            await finishUpgrade();
        } catch (err) {
            setError(err instanceof Error ? err.message : t('goProFailed'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 z-[210] flex items-center justify-center p-4">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between p-5 border-b border-slate-100 dark:border-slate-800">
                    <h2 className="text-lg font-black text-slate-800 dark:text-slate-100">{t('goPro')}</h2>
                    <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600">
                        <XIcon className="w-5 h-5" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">{t('goProDescription')}</p>
                    <p className="text-xs text-slate-500 dir-ltr text-start">{user.email}</p>

                    <form onSubmit={handleSetPassword} className="space-y-3">
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder={t('passwordPlaceholder')}
                            className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 dir-ltr"
                        />
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder={t('confirmPassword')}
                            className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-slate-50 dark:bg-slate-800 dir-ltr"
                        />
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3 bg-brand-sage text-white font-black rounded-xl disabled:opacity-50"
                        >
                            {loading ? t('loading') : t('goProSetPassword')}
                        </button>
                    </form>

                    <div className="relative py-2">
                        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200 dark:border-slate-700" /></div>
                        <div className="relative flex justify-center text-xs uppercase"><span className="bg-white dark:bg-slate-900 px-2 text-slate-400">{t('or')}</span></div>
                    </div>

                    <button
                        type="button"
                        onClick={handleLinkGoogle}
                        disabled={loading}
                        className="w-full py-3 border border-slate-200 dark:border-slate-700 font-black rounded-xl disabled:opacity-50"
                    >
                        {t('goProLinkGoogle')}
                    </button>

                    {error && <p className="text-sm text-red-600">{error}</p>}
                    {!accessId && (
                        <p className="text-xs text-amber-600">{t('goProNoAccessRelation')}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default GoProModal;
